import type { useQueryClient } from '@tanstack/react-query';

/**
 * Optimistically patch a single object (by id) inside every cached
 * ['objects','list',…] query. Used by the publish/unpublish handlers to
 * sync the cache BEFORE the broader invalidate refetch lands, so the
 * surrounding container component never re-renders us with stale
 * publish state. Tolerant of common cache payload shapes — array,
 * `{ items }`, `{ data }`, or `{ results }`.
 *
 * If the cached data shape isn't recognised the original is returned
 * untouched, so the worst case degrades to "no optimistic update" rather
 * than a crash.
 */
export function patchObjectInCaches(
  qc: ReturnType<typeof useQueryClient>,
  objectId: string,
  patch: (obj: any) => any,
): void {
  if (!objectId) return;
  const visit = (arr: any[]): any[] =>
    arr.map((o: any) => (o && o.id === objectId ? patch(o) : o));
  qc.setQueriesData(
    {
      predicate: (q: { queryKey: readonly unknown[] }) =>
        Array.isArray(q.queryKey) &&
        q.queryKey[0] === 'objects' &&
        q.queryKey[1] === 'list',
    },
    (old: any) => {
      if (!old) return old;
      if (Array.isArray(old)) return visit(old);
      if (Array.isArray(old.items)) return { ...old, items: visit(old.items) };
      if (Array.isArray(old.data)) return { ...old, data: visit(old.data) };
      if (Array.isArray(old.results)) return { ...old, results: visit(old.results) };
      return old;
    },
  );
}
